import { Action, createReducer, on } from "@ngrx/store";

import * as UsersActionTypes from "../../actions/users/user.actions";

import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { UserDTO } from "src/app/models/UserDetails/userDTO";
import { UserProfileDTO } from "src/app/models/UserDetails/userProfileDTO";
import { ColumnSorterInfo } from "src/app/models/custom-table/custom-table.model";
import { UserLocaleDTO } from "src/app/models/UserDetails/userLocaleDTO";

export interface UsersState {
  userList: UserDTO[];
  userExcelList: UserDTO[];
  totalUsers: number;
  isLoading: boolean;
  userTableConfig: ColumnSorterInfo[];
  error: Error;
  user: UserDTO;
  created: boolean;
  userProfilesList?: UserProfileDTO[];
  localesList?: UserLocaleDTO[];
  totalRecords: number;
  pageReq: PaginationRequest;
  passwordChanged: boolean;
}

export const initialState: UsersState = {
  userList: null,
  userExcelList: null,
  totalUsers: 0,
  isLoading: false,
  userTableConfig: null,
  error: null,
  user: null,
  created: false,
  userProfilesList: null,
  localesList: null,
  totalRecords: 0,
  pageReq: null,
  passwordChanged: false,
};

const usersReducer = createReducer(
  initialState,
  on(UsersActionTypes.resetUsersState, () => initialState),
  on(UsersActionTypes.getUsers, (state, { options }) => {
    return {
      ...state,
      error: null,
      options: options,
      isLoading: true,
    };
  }),
  on(UsersActionTypes.loadUsers, (state, { response }) => {
    return {
      ...state,
      userList: (response as any)?.result?.items
        ? (response as any).result?.items
        : undefined,
      totalUsers: (response as any)?.result?.size
        ? (response as any)?.result?.size
        : 0,
      error: null,
      isLoading: false,
    };
  }),
  on(UsersActionTypes.getUsersExcel, (state, { options }) => {
    return {
      ...state,
      error: null,
      options: options,
      isLoading: true,
    };
  }),
  on(UsersActionTypes.loadUsersExcel, (state, { response }) => {
    return {
      ...state,
      userExcelList: (response as any)?.result?.items
        ? (response as any)?.result?.items
        : undefined,
      isLoading: false,
      error: null,
    };
  }),
  on(UsersActionTypes.getUsersTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),
  on(UsersActionTypes.loadUsersTableConfig, (state, { response }) => {
    return {
      ...state,
      userTableConfig: (response as any)?.result?.value?.tableColumnInfo ?? undefined,
    };
  }),
  on(UsersActionTypes.saveUsersTableConfig, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(UsersActionTypes.setUser, (state, { user }) => ({
    ...state,
    user: user?.result || null,
    isLoading: false,
    error: null,
  })),
  on(UsersActionTypes.setUserProfiles, (state, { userProfiles }) => ({
    ...state,
    userProfilesList: (<any>userProfiles).result.items || [],
    error: null,
  })),
  on(UsersActionTypes.loadLocales, (state, { response }) => ({
    ...state,
    localesList: (<any>response).result.items || [],
    error: null,
  })),
  on(UsersActionTypes.loadUsersLoadStart, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(UsersActionTypes.loadUsersLoadEnd, (state) => ({
    ...state,
    isLoading: false,
    error: null,
  })),
  on(UsersActionTypes.changePassword, (state) => ({
    ...state,
    isLoading: true,
    passwordChanged: false,
  })),
  on(UsersActionTypes.changePasswordSuccess, (state) => ({
    ...state,
    passwordChanged: true,
  })),
  on(UsersActionTypes.resetUser, (state) => {
    return {
      ...state,
      user: null,
      error: null,
    };
  }),
  on(UsersActionTypes.resetCreation, (state) => {
    return {
      ...state,
      created: true,
    }
  }),
  on(UsersActionTypes.getUser, (state) => {
    return {
      ...state,
      isLoading: true,
      created: false,
    }
  }),
  on(
    UsersActionTypes.createUser,
    UsersActionTypes.updateUser,
    (state) => {
      return {
        ...state,
        isLoading: true,
      };
    }
  )
);

export function reducer(state: UsersState | undefined, action: Action) {
  return usersReducer(state, action);
}
